import { Box, Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import SearchBar from "components/SearchBar";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { EmailTemplate } from "features/Resources/EmailTemplate/models";
import { useMemo, useState } from "react";
import { useEmailTemplateListQuery } from "features/Resources/EmailTemplate/hooks";
import { DisplayRecipients } from "features/Resources/Email/Components/DisplayRecipients";
import { InternalLink } from "components/Link";
import { Text12 } from "components/Text";
import { createQueryString } from "helpers/queryString";
import { emailTemplatePathById } from "helpers/url/pages/v2/actionPaths";
import TemplateListEmptyState from "features/Resources/SharedTemplate/Components/TemplateListEmptyState";
import { TemplateTableDeleteButton } from "features/Resources/SharedTemplate/Components/TemplateTableButtons";
import { DisplayTemplatePaths } from "features/Resources/SharedTemplate/Components/DisplayTemplatePaths";
import TriggerTiming from "components/Display/TriggerTiming";
import { ActionTypeTag } from "design-system";

const columnHelper = createColumnHelper<EmailTemplate>();

const columns = [
  columnHelper.accessor((row) => row, {
    id: "type",
    header: "",
    cell: () => <ActionTypeTag.IconBadge actionType="email" />,
    maxSize: 43,
    minSize: 43,
    size: 43,
    enableSorting: false,
    meta: {
      sx: {
        paddingLeft: "24px !important",
        paddingRight: "0 !important",
        width: "56px !important",
        maxWidth: "56px !important",
        minWidth: "56px !important",
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    enableSorting: false,
    cell: (row) => (
      <Box width="fit-content">
        <InternalLink
          to={emailTemplatePathById(row.getValue().id)}
          textDecoration="none"
          _hover={{ textDecoration: "none" }}
          width="fit-content"
        >
          <Text12 wordBreak="break-word" fontWeight="bold">
            {row.getValue()?.name}
          </Text12>
        </InternalLink>
      </Box>
    ),
    meta: {
      sx: {
        paddingLeft: "0 !important",
      },
      headerHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "recipients",
    header: "Recipients",
    enableSorting: false,
    cell: (row) => <DisplayRecipients email={row.getValue()} />,
  }),
  columnHelper.accessor((row) => row, {
    id: "scheduled",
    header: "Deliver",
    enableSorting: false,
    cell: (row) => <TriggerTiming descriptor={row.getValue().deliverAtTrigger?.data?.descriptor} />,
    meta: {
      sx: {
        width: "160px",
        maxWidth: "160px",
        minWidth: "160px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "pathIds",
    header: "Paths",
    enableSorting: false,
    cell: (row) => <DisplayTemplatePaths template={row.getValue()} />,
    meta: {
      sx: {
        width: "100px",
        maxWidth: "100px",
        minWidth: "100px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "buttons",
    header: "",
    enableSorting: false,
    cell: (row) => (
      <TemplateTableDeleteButton templateSummary={{ templateType: "email_template", template: row.getValue() }} />
    ),
    meta: {
      sx: {
        width: "60px",
        maxWidth: "60px",
        minWidth: "60px",
      },
      headerHStackProps: { justifyContent: "flex-end" },
      cellHStackProps: { justifyContent: "flex-end" },
    },
  }),
];

export default function EmailTemplates() {
  const [searchText, setSearchText] = useState("");

  const queryString = useMemo(
    () =>
      createQueryString({
        pagination: { pageIndex: 0, pageSize: 200 },
        additionalQueries: {
          q: searchText && searchText.length > 2 ? searchText : undefined,
        },
      }),
    [searchText],
  );

  const { data } = useEmailTemplateListQuery(queryString);
  const sortedData = useMemo(
    () =>
      [...(data || [])].sort(
        (a, b) => (a.deliverAtTrigger?.sortValue ?? Infinity) - (b.deliverAtTrigger?.sortValue ?? Infinity),
      ),
    [data],
  );

  return (
    <Stack direction="column" gap="4" w="100%">
      <Stack direction="row" w="100%" alignItems="center" justifyContent="flex-start">
        <SearchBar minWidth="500" onInput={setSearchText} />
      </Stack>
      <ControlledDataTable<EmailTemplate>
        data={sortedData}
        columns={columns}
        initialPagination={{ pageSize: 200, pageIndex: 0 }}
        emptyStateComponent={<TemplateListEmptyState actionType="email" hasActiveFilters={searchText.length > 2} />}
      />
    </Stack>
  );
}
